import { SiteStage, Workspace } from "@gql/sites-api/graphql";

import { Select } from "react-migration/components/Select";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export type SelectWorkspace = Pick<Workspace, "id" | "title">;
export type SelectStage = Pick<SiteStage, "_id" | "name" | "color" | "hidden">;

export interface WorkspaceSelectProps {
  selectedWorkspace?: SelectWorkspace;
  workspaces: SelectWorkspace[];
  loading: boolean;
  onChange: (workspace: SelectWorkspace | undefined) => void;
}

export interface StageSelectProps {
  selectedStage?: SelectStage;
  stages: SelectStage[];
  loading: boolean;
  onChange: (stage: SelectStage | undefined) => void;
}

export const WorkspaceSelect = ({ selectedWorkspace, onChange, workspaces, loading }: WorkspaceSelectProps) => {
  const hasNoWorkspaces = loading || !workspaces?.length;

  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="flex flex-col overflow-hidden gap-y-1 grow z-52" data-testid="stage-select">
      <Select
        value={selectedWorkspace?.id}
        disabled={loading || hasNoWorkspaces}
        onValueChange={(value) => onChange(workspaces.find((workspace) => workspace.id === value))}
        triggerProps={{ "data-testid": "workspace-select-dropdown" }}
        size={Select.Size.XS}
        onOpenChange={(open) => {
          setDropdownOpen(open);
        }}
      >
        {workspaces.map((workspace) => (
          <Select.Option
            key={workspace.id}
            value={workspace.id}
            title={workspace.title}
            className={clsx(
              selectedWorkspace?.id === workspace.id &&
                dropdownOpen &&
                "bg-white text-text-base hover:bg-white hover:text-text-base pointer-events-none select-none"
            )}
            itemClassName="flex flex-row gap-x-1"
          >
            <span className="truncate">{workspace.title}</span>
            {selectedWorkspace?.id === workspace.id && dropdownOpen && (
              <span className="text-text-base-primary-disabled font-normal">(selected)</span>
            )}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export const StageSelect = ({ selectedStage, onChange, stages, loading }: StageSelectProps) => {
  const { t } = useTranslation();
  const hasNoStages = loading || !stages?.length;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="flex flex-col overflow-hidden gap-y-1 grow z-52" data-testid="stage-select">
      <Select
        value={selectedStage?._id}
        disabled={loading || hasNoStages}
        onValueChange={(value) => onChange(stages.find((stage) => stage._id === value))}
        triggerProps={{ "data-testid": "stage-select-dropdown" }}
        size={Select.Size.XS}
        side="bottom"
        align="end"
        alignOffset={50}
        onOpenChange={(open) => {
          setDropdownOpen(open);
        }}
      >
        {(stages || []).map(({ _id, name, color, hidden }) => (
          <Select.Option
            key={_id}
            value={_id}
            title={name ?? ""}
            className={clsx(
              selectedStage?._id === _id &&
                dropdownOpen &&
                "bg-white text-text-base hover:bg-white hover:text-text-base pointer-events-none select-none"
            )}
            itemClassName="flex flex-row gap-x-1 p-0!"
          >
            <div className="flex items-center justify-start gap-x-2 truncate">
              <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: color ?? undefined }} />
              {`${name} ${hidden ? t("sites.save_site.save_site_dropdown.hidden") : ""}`}
            </div>
            {selectedStage?._id === _id && dropdownOpen && (
              <span className="text-text-base-primary-disabled flex items-center">(selected)</span>
            )}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};
